import { useMutation, useQuery } from '@apollo/client/react'
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Heading,
  HStack,
  Icon,
  Menu,
  Portal,
  Presence,
  Spinner,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import { UserAvatar } from '@src/components/sco/Account/UserAvatar'
import { WIL_AVATAR } from '@src/components/sco/Advisors/constants'
import { useWilTool } from '@src/components/sco/Advisors/hooks/useWilTool'
import { sessionTitle } from '@src/components/sco/Advisors/utils/wilSessionTitle'
import { AppLayoutToolbar } from '@src/components/sco/AppLayout/Toolbar/AppLayoutToolbar'
import { useChatContext } from '@src/components/sco/Chat/ChatContext'
import { ChatDirectThreadList } from '@src/components/sco/Chat/ChatDirectThreadList'
import { ChatMessageComposer } from '@src/components/sco/Chat/ChatMessageComposer'
import { ChatThread } from '@src/components/sco/Chat/ChatThread'
import { DirectChatThreadAmongstUsers } from '@src/components/sco/Chat/ChatThreadAmongstUsers'
import { UserProfileChatHeader } from '@src/components/sco/Members/UserProfile'
import { Paragraph } from '@src/components/sco/Paragraph'
import { PaywalledAction } from '@src/components/sco/PaywalledAction'
import { WIL_ONBOARDING_TOOL_SLUG } from '@src/components/sco/SignupRequestChatIntake/constants'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import type { ScoToolSession, ScoUser } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { LuArrowRight, LuCheck, LuChevronDown, LuInbox, LuMessageSquarePlus } from 'react-icons/lu'

const REQUEST_PANEL_QUERY = gql`
  query RequestDiscussionPanel($groupRequestId: String!) {
    groupRequest(groupRequestId: $groupRequestId) {
      id
      canUserEdit
      requestingUserId
      wilAiToolSession {
        id
        toolSessionId
        title
        createdAt
        updatedAt
        generatedSummary
        threadId
        thread {
          id
          threadId
          lastActiveAt
          commentsCount
          comments(pageSize: 5, order: [["createdAt", "asc"]]) {
            id
            items {
              id
              body
              createdByUserId
              createdByUser {
                id
                userId
              }
            }
          }
        }
      }
    }
  }
`

const REQUEST_WIL_SESSIONS_QUERY = gql`
  query RequestWilAiSessions(
    $toolId: String
    $userId: String
    $attachmentType: String
    $attachmentId: String
    $order: [[String]]
    $pageSize: Int
  ) {
    scoToolSessions(
      toolId: $toolId
      userId: $userId
      attachmentType: $attachmentType
      attachmentId: $attachmentId
      order: $order
      pageSize: $pageSize
    ) {
      id
      items {
        id
        toolSessionId
        title
        createdAt
        updatedAt
        generatedSummary
        threadId
        thread {
          id
          threadId
          lastActiveAt
          commentsCount
          comments(pageSize: 5, order: [["createdAt", "asc"]]) {
            id
            items {
              id
              body
              createdByUserId
              createdByUser {
                id
                userId
              }
            }
          }
        }
      }
    }
  }
`

const UPSERT_TOOL_SESSION = gql`
  mutation CreateRequestWilSession($input: UpsertToolSessionInput!) {
    upsertToolSession(input: $input) {
      toolSession {
        id
        toolSessionId
        title
        threadId
        thread {
          id
          threadId
        }
      }
    }
  }
`

const SESSION_DETAIL_QUERY = gql`
  query RequestWilSessionDetail($toolSessionId: String!) {
    scoToolSession(toolSessionId: $toolSessionId) {
      id
      toolSessionId
      title
      createdAt
      updatedAt
      generatedSummary
      threadId
      thread {
        id
        threadId
        lastActiveAt
        commentsCount
        comments(pageSize: 5, order: [["createdAt", "asc"]]) {
          id
          items {
            id
            body
            createdByUserId
            createdByUser {
              id
              userId
            }
          }
        }
      }
    }
  }
`

export type RequestDiscussionPanelProps = {
  groupRequestId: string
  showPanelToggles?: boolean
}

export const RequestDiscussionPanel: React.FC<RequestDiscussionPanelProps> = props => {
  const chatContext = useChatContext()
  const { currentUser } = useCurrentUser()
  const [showMessageList, setShowMessageList] = useState(true)
  const { getBreakpointValue } = useBreakpointContext()
  const { toolId, toolUserId, wilDisplayName } = useWilTool()
  const {
    toolUserId: onboardingToolUserId,
    wilToolUser: onboardingToolUser,
    toolIconUrl: onboardingIconUrl,
  } = useWilTool(WIL_ONBOARDING_TOOL_SLUG)
  const wilAvatarUser = useMemo(
    () =>
      ({
        userId: onboardingToolUserId || onboardingToolUser?.userId,
        firstName: onboardingToolUser?.firstName,
        lastName: onboardingToolUser?.lastName,
        fullName: onboardingToolUser?.fullName,
        displayName: onboardingToolUser?.displayName,
        slug: onboardingToolUser?.slug,
        pictureUrl: onboardingToolUser?.pictureUrl || onboardingIconUrl || WIL_AVATAR,
      }) as ScoUser,
    [onboardingToolUserId, onboardingToolUser, onboardingIconUrl]
  )

  const [activeToolSessionId, setActiveToolSessionId] = useState<string | undefined>()
  // Chat/Apollo loading flags differ between SSR and the first client paint (skip +
  // cache-and-network). Hold the same spinner on both until mount so we don't
  // hydrate a <p> "not available" against a <Spinner>.
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => {
    setHasMounted(true)
  }, [])

  const query = useQuery(REQUEST_PANEL_QUERY, {
    variables: { groupRequestId: props.groupRequestId },
  })

  const groupRequest = query.data?.groupRequest
  const defaultSession = groupRequest?.wilAiToolSession as ScoToolSession | undefined
  const defaultTabValue = groupRequest?.canUserEdit ? 'tab-0' : 'tab-2'

  const sessionsQuery = useQuery(REQUEST_WIL_SESSIONS_QUERY, {
    variables: {
      toolId,
      userId: groupRequest?.requestingUserId || currentUser?.userId,
      attachmentType: 'GroupRequest',
      attachmentId: props.groupRequestId,
      order: [['updatedAt', 'desc']],
      pageSize: 50,
    },
    skip: !toolId || !groupRequest?.canUserEdit || !props.groupRequestId,
    fetchPolicy: 'cache-and-network',
  })

  const sessions = useMemo(() => {
    const items = (sessionsQuery.data?.scoToolSessions?.items || []).filter(Boolean) as ScoToolSession[]
    if (items.length > 0) {
      return items
    }
    // Fallback while list is empty/loading but ensure-session already returned.
    return defaultSession?.toolSessionId ? [defaultSession] : []
  }, [sessionsQuery.data?.scoToolSessions?.items, defaultSession])

  const resolvedToolSessionId =
    activeToolSessionId ||
    (defaultSession?.toolSessionId ? String(defaultSession.toolSessionId) : undefined) ||
    (sessions[0]?.toolSessionId ? String(sessions[0].toolSessionId) : undefined)

  // Seed selection from ensure-session (wilAiToolSession) or first listed chat.
  useEffect(() => {
    if (activeToolSessionId) {
      return
    }
    if (resolvedToolSessionId) {
      setActiveToolSessionId(resolvedToolSessionId)
    }
  }, [activeToolSessionId, resolvedToolSessionId])

  // If the selected id drops out of the list after a refetch, fall back.
  useEffect(() => {
    if (!activeToolSessionId || sessions.length === 0) {
      return
    }
    const stillThere = sessions.some(s => s.toolSessionId === activeToolSessionId)
    if (!stillThere && defaultSession?.toolSessionId) {
      setActiveToolSessionId(String(defaultSession.toolSessionId))
    }
  }, [activeToolSessionId, sessions, defaultSession?.toolSessionId])

  const activeSessionQuery = useQuery(SESSION_DETAIL_QUERY, {
    variables: { toolSessionId: resolvedToolSessionId },
    skip: !resolvedToolSessionId,
    fetchPolicy: 'cache-and-network',
  })

  const activeSession = useMemo(() => {
    const fromDetail = activeSessionQuery.data?.scoToolSession as ScoToolSession | undefined
    if (fromDetail?.toolSessionId === resolvedToolSessionId) {
      return fromDetail
    }
    return sessions.find(s => s.toolSessionId === resolvedToolSessionId) || defaultSession
  }, [activeSessionQuery.data?.scoToolSession, resolvedToolSessionId, sessions, defaultSession])

  const activeThreadId = useMemo(() => {
    const id = activeSession?.threadId || activeSession?.thread?.threadId
    return id ? String(id) : null
  }, [activeSession?.threadId, activeSession?.thread?.threadId])

  const [createSession, { loading: creatingSession }] = useMutation(UPSERT_TOOL_SESSION)
  const [upsertChatComment, upsertChatCommentMutation] = useMutation(
    ChatMessageComposer.mutations.upsertChatComment()
  )

  const startNewSession = useCallback(async () => {
    if (!toolId || creatingSession) {
      return
    }
    try {
      const res = await createSession({
        variables: {
          input: {
            toolId,
            attachmentType: 'GroupRequest',
            attachmentId: props.groupRequestId,
          },
        },
      })
      const created = res.data?.upsertToolSession?.toolSession
      const newId = created?.toolSessionId
      if (newId) {
        await sessionsQuery.refetch()
        setActiveToolSessionId(String(newId))
      }
    } catch (err) {
      console.error('Failed to create goal Wil session', err)
    }
  }, [toolId, creatingSession, createSession, props.groupRequestId, sessionsQuery])

  const onStartClick = (message: string | null = null) => {
    if (!activeThreadId) {
      alert('Error: Thread not available.')
      return
    }
    void upsertChatComment({
      variables: {
        input: {
          threadId: activeThreadId,
          body: message || 'Get Started',
        },
      },
    }).then(() => {
      void activeSessionQuery.refetch()
      void sessionsQuery.refetch()
    })
  }

  if (!hasMounted) {
    return (
      <Center padding={4} width={'full'}>
        <Spinner />
      </Center>
    )
  }

  if (query.error) {
    return <Center padding={4}>{query.error.toString()}</Center>
  }

  // Prefer cached data over a full-panel spinner when Apollo is background-refetching.
  if (!groupRequest) {
    if (query.loading) {
      return (
        <Center padding={4} width={'full'}>
          <Spinner />
        </Center>
      )
    }
    return (
      <Center padding={4} width={'full'}>
        <Paragraph>Chat unavailable.</Paragraph>
      </Center>
    )
  }

  return (
    <Tabs.Root
      alignItems={'stretch'}
      defaultValue={defaultTabValue}
      display={'flex'}
      flexDirection={'column'}
      height={'full'}
      justifyContent={'stretch'}
      minHeight={0}
      overflow={'hidden'}
      position={'relative'}
      size={'sm'}
      variant={'enclosed'}
      width={'full'}
    >
      {groupRequest.canUserEdit && (
        <Tabs.Content
          alignItems={'stretch'}
          display={'flex'}
          flex={1}
          flexDirection={'column'}
          justifyContent={'stretch'}
          minHeight={0}
          overflow={'hidden'}
          padding={0}
          value={'tab-0'}
        >
          <AppLayoutToolbar
            hideBreadcrumbs
            hideLeftToggleButton
            hideRightToggleButton={!props.showPanelToggles}
            boxProps={{
              // Cap at standard toolbar height. Default py=3 (24px) + sm button (36px) = 60px
              // which blows past 3.5rem (56px); py=2 leaves room for the sm Chats control.
              height: '3.5rem',
              minHeight: '3.5rem',
              maxHeight: '3.5rem',
              py: 2,
              overflow: 'hidden',
            }}
            leftElements={
              <HStack align={'center'} flex={1} gap={2} maxH={'full'} minWidth={0}>
                <UserAvatar
                  disablePresence
                  isAI
                  noLink
                  avatarProps={{ size: 'xs', flexShrink: 0 }}
                  user={wilAvatarUser}
                  userId={onboardingToolUserId}
                />
                <Text flex={1} fontSize={'sm'} fontWeight={'semibold'} lineClamp={1} minWidth={0}>
                  {wilDisplayName}
                </Text>
              </HStack>
            }
            rightElements={
              <Menu.Root positioning={{ placement: 'bottom-end' }}>
                <Menu.Trigger asChild>
                  <Button flexShrink={0} size={'sm'} variant={'outline'}>
                    Chats
                    <LuChevronDown />
                  </Button>
                </Menu.Trigger>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content maxH={'min(24rem, 70vh)'} maxW={'20rem'} minW={'16rem'} overflowY={'auto'}>
                      {sessions.map(session => {
                        const id = session.toolSessionId
                        if (!id) {
                          return null
                        }
                        const isActive = id === resolvedToolSessionId
                        return (
                          <Menu.Item
                            key={id}
                            closeOnSelect
                            value={String(id)}
                            onClick={() => {
                              if (!isActive) {
                                setActiveToolSessionId(String(id))
                              }
                            }}
                          >
                            <Text flex={1} fontWeight={isActive ? 'semibold' : 'normal'} lineClamp={1}>
                              {sessionTitle(session, toolUserId)}
                            </Text>
                            {isActive ? <LuCheck /> : null}
                          </Menu.Item>
                        )
                      })}
                      {sessions.length > 0 ? <Menu.Separator /> : null}
                      <Menu.Item
                        disabled={creatingSession}
                        value={'new-session'}
                        onClick={() => {
                          void startNewSession()
                        }}
                      >
                        <LuMessageSquarePlus />
                        New Chat
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            }
          />

          {activeThreadId ? (
            <Box display={'flex'} flex={1} flexDirection={'column'} minHeight={0} overflow={'hidden'}>
              <ChatThread
                key={activeThreadId}
                bypassContentPermissions={true}
                hideHeader={true}
                threadId={activeThreadId}
                chatMessageListProps={{
                  blankState: (
                    <VStack align={'center'} gap={4} height={'full'} justify={'center'} padding={4}>
                      <HStack align={'center'} gap={2} justify={'center'}>
                        <UserAvatar
                          disablePresence
                          isAI
                          noLink
                          avatarProps={{ size: 'lg' }}
                          user={wilAvatarUser}
                          userId={onboardingToolUserId}
                        />
                        <Heading marginTop={1} size={'2xl'}>
                          {wilDisplayName}
                        </Heading>
                      </HStack>

                      <Box padding={getBreakpointValue([0, 0, 4, 4, 4])} width={'full'}>
                        <Presence
                          animationDuration={'moderate'}
                          present={true}
                          animationName={{
                            _open: 'slide-from-bottom, fade-in',
                            _closed: 'slide-to-bottom, fade-out',
                          }}
                        >
                          <VStack align={'stretch'} gap={2} justify={'stretch'} width={'full'}>
                            <Text fontSize={'sm'} textAlign={'center'}>
                              {'Hey '}
                              {currentUser?.firstName}, I'm here to help you achieve this goal.
                            </Text>
                            <Text fontSize={'sm'} marginBottom={4} textAlign={'center'}>
                              Where should we start?
                            </Text>
                            <ButtonGroup attached orientation={'vertical'} variant={'outline'}>
                              {[
                                'How do I accomplish this goal?',
                                'Explain how this platform works',
                                'Which Members match my Goal?',
                              ].map((startMessage, idx) => (
                                <Button
                                  key={idx}
                                  borderRadius={'md'}
                                  height={'auto'}
                                  justifyContent={'flex-start'}
                                  loading={upsertChatCommentMutation.loading}
                                  padding={4}
                                  size={'sm'}
                                  textAlign={'left'}
                                  variant={'outline'}
                                  whiteSpace={'normal'}
                                  width={'full'}
                                  onClick={() => onStartClick(startMessage)}
                                >
                                  {startMessage}
                                  <LuArrowRight />
                                </Button>
                              ))}
                            </ButtonGroup>
                          </VStack>
                        </Presence>
                      </Box>
                    </VStack>
                  ),
                }}
              />
            </Box>
          ) : query.loading || sessionsQuery.loading || activeSessionQuery.loading ? (
            <Center flex={1} padding={4}>
              <Spinner />
            </Center>
          ) : (
            <Center flex={1} padding={4}>
              <Paragraph textAlign={'center'}>Goal Advisor not available.</Paragraph>
            </Center>
          )}
        </Tabs.Content>
      )}
      <Tabs.Content
        display={'flex'}
        flex={1}
        flexDirection={'column'}
        minHeight={0}
        overflow={'hidden'}
        padding={0}
        value={'tab-2'}
      >
        {showMessageList && groupRequest.canUserEdit ? (
          <>
            {chatContext.activeThreadId ? (
              <ChatThread
                threadId={chatContext.activeThreadId}
                renderCustomHeader={threadId => {
                  return (
                    <UserProfileChatHeader
                      threadId={threadId}
                      onClose={() => chatContext.setActiveThreadId(null)}
                    />
                  )
                }}
              />
            ) : (
              <ChatDirectThreadList
                attachmentId={props.groupRequestId}
                attachmentType={'GroupRequest'}
                blankState={
                  <VStack align={'center'} height={'full'} justify={'center'} padding={4}>
                    <Icon asChild>
                      <LuInbox />
                    </Icon>
                    <Text>No private messages yet.</Text>
                    {currentUser?.userId !== groupRequest.requestingUserId && (
                      <Button onClick={() => setShowMessageList(false)}>Start a Chat</Button>
                    )}
                  </VStack>
                }
              />
            )}
          </>
        ) : (
          <PaywalledAction
            contentPermission={'canAccessChat'}
            intent={'discussion-join'}
            upgradeBoxProps={{ width: 'auto', margin: 8 }}
            variant={'boxed'}
          >
            <DirectChatThreadAmongstUsers
              attachmentId={props.groupRequestId}
              attachmentType={'GroupRequest'}
              userIds={[currentUser.userId, groupRequest.requestingUserId]}
            />
          </PaywalledAction>
        )}
      </Tabs.Content>
    </Tabs.Root>
  )
}
